import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { MenuAction } from '../../models/menuAction';
import { MaterialIconVariant } from '../../models/portalActions';
import store from '../../store/store';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-menu',
    styleUrl: 'portal-menu.scss',
    shadow: true,
})
export class PortalMenu {
    @Element() $el: HTMLPortalMenuElement;

    /**
    * Theme override to bypass store state
    * @memberof PortalMenuItem
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Data passed to this component will be emitted as an additinal data.
     * @memberof PortalMenu
     */
    @Prop() callbackdata?: Object;

    /**
     *  Badge value
     * @memberof PortalMenu
     */
    @Prop() badge: string | null = null;

    /**
     * Action array to be placed in card footer
     *  @memberof PortalMenu
     */
    @Prop() menuList: MenuAction[] | string;

    /**
     * Track if menu is currently shown
     */
    @State() isMenuOpen: boolean = false;

    /**
     * Track if text is copied
     */
    @State() isCopied: boolean = false;

    /**
     * Define custom event for icon click
     */
    @Event({
        eventName: 'actionClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) actionClicked: EventEmitter;

    /**
     *  Private variable to hold card data
     *  @memberof PortalMenu
     */
    private _menuListArr: MenuAction[];

    private selectedMenuItem: HTMLElement | null = null;
    private selectedMenuIndex: number = -1;
    private activeDropdownMenuSurface: HTMLPortalMenuSurfaceElement;
    private menuListArray: HTMLElement[] = [];

    @Listen('portalMenuSurfaceOpened')
    onPortalMenuSurfaceOpened(_event: CustomEvent) {
        this.isMenuOpen = true;
    }

    @Listen('portalMenuSurfaceClosed')
    onPortalMenuSurfaceClosed(_event: CustomEvent) {
        this.isMenuOpen = false;
        this.isCopied = false;
        this.resetActiveMenu();
        this.$el
            .shadowRoot
            ?.querySelector('.menu-surface')
            ?.shadowRoot
            ?.querySelector('.portal-modal')
            ?.setAttribute('aria-activedescendant', '');
        this.clearMenuListArray();
    }

    /**
    * Stencil lifecycle method: Watch value change on Actions attribute
    * @param newValue
    */
    @Watch('menuList')
    arrayDataWatcher(newValue: MenuAction[] | string) {
        if (typeof newValue === 'string') {
            this._menuListArr = JSON.parse(newValue);
        } else {
            this._menuListArr = newValue;
        }
    }

    /**
     * ComponentWillLoad Lifecycle
     */
    componentWillLoad() {
        this.arrayDataWatcher(this.menuList);
    }

    @Listen('portalMenuItemClick')
    menuClicked(event: CustomEvent) {
        this.actionClicked.emit({
            ...event.detail.data,
            ...this.callbackdata,
        });
        this.activeDropdownMenuSurface.closeMenu();
        this.clearMenuListArray();
    }

    render() {
        return (
            <Host class={getTheme(store, this.theme)}>
                <span data-test="portal-menu" class="menu-container"
                    onClick={(ev) => {
                        this.toggleMenu(ev);
                    }}
                    onKeyDown={(ev) => this.menuKeyDown(ev)}>
                    <slot></slot>
                    {this.badge && <span class="portal-badge" >{this.badge}</span>}
                </span>
                <portal-menu-surface
                    class="menu-surface"
                    containerRole="menu"
                    aria-expanded={`${this.isMenuOpen}`}
                    theme={this.theme}
                    left='0'
                    ref={(elem) => this.activeDropdownMenuSurface = elem as HTMLPortalMenuSurfaceElement}>
                    {
                        this._menuListArr?.map((item: MenuAction, idx: number) => {
                            switch (item.type) {
                                case 'text':
                                case 'icon':
                                case 'overflow':
                                    return <portal-menu-item
                                        itemRole="menuitem"
                                        id={item.id}
                                        icon={item.icon}
                                        variant={item.variant as MaterialIconVariant}
                                        menuid={item.id}
                                        data-cy={item.id}
                                        disabled={item.disabled}
                                        data={{ 'actionId': item.id }}
                                        primaryText={item.name}
                                        secondaryText={item.description}
                                        theme={this.theme}
                                        appearance={item.appearance} />;
                                case 'link':
                                    return <portal-menu-link
                                        itemRole="menuitem"
                                        id={this.getMenuItemId(idx)}
                                        text={item.name}
                                        href={item.href}
                                        onClick={item.customEvent ? () => Logger.logCustomEvent(item.customEvent!) : undefined} />;
                                case 'divider':
                                    return <div id={this.getMenuItemId(idx)} class="menu-divider"></div>;
                                case 'copy':
                                    return <div role="menuitem"
                                        class='menu-item-content'
                                        id={item.id}
                                        data-cy={item.id}>
                                        {item.name}
                                        {this.isCopied ?
                                            <portal-icon-button
                                                class='check-icon'
                                                title={t('copy-clipboard', getLanguage(store))}
                                                name="check_text_copied"
                                                ariaLabel={t('text-copied', getLanguage(store))}
                                                theme={this.theme}
                                                cssClass={'copy-button'}
                                                iconSize='16px'
                                                onClick={() => {
                                                    this.isCopied = false;
                                                }}
                                                data-test={`${item.id}-text-copied`}
                                            ></portal-icon-button> :
                                            <portal-icon-button
                                                class='copy-icon'
                                                title={t('copy-clipboard', getLanguage(store))}
                                                name="copy_text"
                                                ariaLabel={t('copy-text', getLanguage(store))}
                                                theme={this.theme}
                                                cssClass={'copy-button'}
                                                iconSize='16px'
                                                onClick={() => {
                                                    navigator.clipboard.writeText(item.name);
                                                    this.isCopied = true;
                                                }}
                                                data-test={`${item.id}-copy-text`}
                                            ></portal-icon-button>}
                                    </div>;
                                case 'header':
                                    return <div id={this.getMenuItemId(idx)}
                                        role="heading"
                                        aria-level="3"
                                        class="menu-item-header"
                                    >{item.text}</div>;
                            }
                        })}
                </portal-menu-surface>
            </Host>
        );
    }

    private getMenuItemId(idx: number) {
        return `menu-item-${idx}`;
    }

    /**
     * Function to handle menu show/hide
     */
    private toggleMenu(ev: MouseEvent | KeyboardEvent, shouldFocusMenuItem = false) {
        ev.stopImmediatePropagation();
        const currElem = ev.currentTarget as HTMLElement | null;
        if (!currElem || currElem.classList.contains('disabled')) {
            return;
        }
        store.set('activeDropdownMenuSurface', this.activeDropdownMenuSurface);
        this.getMenuList();
        this.resetActiveMenu();
        this.activeDropdownMenuSurface.toggleMenu(currElem);
        if (shouldFocusMenuItem) {
            // workaround : Without the delay the focus is a no OP.
            // Only with the delay the focus gets the item to the focused state.
            // issue is due to DOM taking time to register the focus call.
            setTimeout(() => {
                this.setMenuItemActive(this.menuListArray, 0);
            }, 50);
        }
    }

    /**
     * Prepare menu list which are not disabled
     * @memberof PortalMenu
     */
    private getMenuList() {
        this.menuListArray = [];// Reset array before filling data
        const activeSurface = store.get('activeDropdownMenuSurface') as HTMLPortalMenuSurfaceElement;
        if (activeSurface) {
            const menuItemList = activeSurface.querySelectorAll('portal-menu-link,portal-menu-item');

            menuItemList.forEach((element: HTMLElement) => {
                const btnEl = element.shadowRoot?.querySelector('a,button:not([disabled])') as HTMLElement;
                if (btnEl) {
                    this.menuListArray.push(btnEl);
                }
            });
        }
    }

    /**
    * Remove `menu-active` class from menu item
    */
    private resetActiveMenu() {
        if (this.selectedMenuItem) {
            this.selectedMenuIndex = -1;
            this.selectedMenuItem.classList.remove('menu-active');
            this.selectedMenuItem = null;
        }
    }

    /**
     * Function called on keyboard press.
     * This function handles the keyboard button events and provides keyboard navigation for menu
     * @param ev
     */
    private menuKeyDown(ev: KeyboardEvent) {
        const activeSurface = store.get('activeDropdownMenuSurface') as HTMLPortalMenuSurfaceElement;
        let nextActiveIndex = 0;
        let prevActiveIndex = 0;
        switch (ev.key) {
            case 'ArrowUp':
                if (this.isMenuOpen) {
                    ev.preventDefault();
                    prevActiveIndex = this.selectedMenuIndex - 1;
                    this.resetActiveMenu();
                    // Keep the index within bounds
                    if (prevActiveIndex < 0) {
                        prevActiveIndex = this.menuListArray.length - 1;
                    }
                    this.setMenuItemActive(this.menuListArray, prevActiveIndex);
                }
                break;
            case 'ArrowDown':
                if (this.isMenuOpen) {
                    ev.preventDefault();
                    nextActiveIndex = this.selectedMenuIndex + 1;
                    this.resetActiveMenu();
                    // Keep the index within bounds
                    if (nextActiveIndex >= this.menuListArray.length) {
                        nextActiveIndex = 0;
                    }
                    this.setMenuItemActive(this.menuListArray, nextActiveIndex);
                }
                break;
            case 'Enter':
            case 'Space':
            case ' ': // Some browsers bubble up the space keycode as this (' ' vs 'Space')
                ev.preventDefault();
                if (this.isMenuOpen) {
                    this.selectedMenuItem?.click();
                    this.clearMenuListArray();
                } else {
                    this.toggleMenu(ev, true);
                }
                this.resetActiveMenu();
                break;
            case 'Escape':
            case 'Tab':
                if (this.isMenuOpen) {
                    this.resetActiveMenu();
                    activeSurface?.closeMenu();
                    this.clearMenuListArray();
                }
                break;
        }
    }

    /**
     * This function will set `menu-active` class to the next menu item when navigated through keyboard
     * @param menuListArray : MenuList array
     * @param nextActiveIndex : next index for menu item
     */
    private setMenuItemActive(menuListArray: HTMLElement[], nextActiveIndex: number) {
        const activeElem = menuListArray[nextActiveIndex];
        activeElem?.classList?.add('menu-active');
        this.selectedMenuItem = activeElem;
        this.selectedMenuIndex = nextActiveIndex;
        const menuLinkArray = this.getMenuLinkListArray();
        const activeMenuLink = menuLinkArray[nextActiveIndex];
        this.$el
            .shadowRoot
            ?.querySelector('.menu-surface')
            ?.shadowRoot
            ?.querySelector('.portal-modal')
            ?.setAttribute('aria-activedescendant', `${activeMenuLink.id}`);
    }

    /**
     * Prepare menu-link list which are not disabled
     */
    private getMenuLinkListArray() {
        const menuLinkListArray: HTMLElement[] = [];
        const activeSurface = store.get('activeDropdownMenuSurface') as HTMLPortalMenuSurfaceElement;
        if (activeSurface) {
            const menuItemList = activeSurface.querySelectorAll('portal-menu-link,portal-menu-item');

            menuItemList.forEach((element: HTMLElement) => {
                const btnEl = element.shadowRoot?.querySelector('a,button:not([disabled])') as HTMLElement;
                if (btnEl) {
                    menuLinkListArray.push(element);
                }
            });
        }
        return menuLinkListArray;
    }

    /**
     * Function to clear the menulist array when menu is closed.
     */
    private clearMenuListArray() {
        this.menuListArray = [];
    }
}
